/** Histórico de atividades realizadas por mês, com o treino vinculado. */
import { useState } from "react";
import { api } from "../api/client";
import type { Activity } from "../api/types";
import { localTime, SPORT_LABEL } from "../lib/activities";
import { formatDuration, formatPace, metersToKm } from "../lib/format";
import { addDays, fmtDayHeader, fmtMonth, iso, parseIso } from "../lib/training";
import { useAsync } from "../lib/useAsync";
import { Link, navigate } from "../router/router";
import { Badge, Button, EmptyState, ErrorState, Loading } from "./ui";
import { t, tn } from "../i18n";

export function ActivityList({
  listPath,
  hrefFor,
  reloadKey = 0,
}: {
  listPath: string;
  hrefFor: (a: Activity) => string;
  reloadKey?: number;
}) {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const start = month;
  const end = addDays(new Date(month.getFullYear(), month.getMonth() + 1, 1), -1);
  const list = useAsync(() => api.get<Activity[]>(listPath, { from: iso(start), to: iso(end) }), [listPath, iso(start), reloadKey]);
  const total = (list.data ?? []).reduce((acc, a) => acc + (a.distance_m ?? 0), 0);
  const step = (n: number) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));

  return (
    <section className="section" aria-labelledby="historico">
      <div className="cal-toolbar">
        <h2 id="historico" style={{ margin: 0 }}>
          {t("Atividades realizadas")}
        </h2>
        <div className="cal-nav">
          <Button size="sm" onClick={() => step(-1)} aria-label={t("Mês anterior")}>
            ‹
          </Button>
          <strong className="cal-range">{fmtMonth(month)}</strong>
          <Button size="sm" onClick={() => step(1)} aria-label={t("Próximo mês")}>
            ›
          </Button>
          {list.data && list.data.length > 0 && (
            <span className="muted num">
              {tn("{n} atividade", "{n} atividades", list.data.length)} · {metersToKm(total)}
            </span>
          )}
        </div>
      </div>
      {list.error && <ErrorState error={list.error} onRetry={list.reload} />}
      {!list.data && list.loading && <Loading />}
      {list.data && list.data.length === 0 && (
        <div className="panel">
          <EmptyState title={t("Nenhuma atividade neste mês")}>{t("Importe um arquivo do relógio para ver distância, ritmo e a comparação com o treino.")}</EmptyState>
        </div>
      )}
      {list.data && list.data.length > 0 && (
        <div className="table-wrap">
          <table className="data responsive activity-table">
            <thead>
              <tr>
                <th>{t("Data")}</th>
                <th>{t("Atividade")}</th>
                <th className="right">{t("Distância")}</th>
                <th className="right">{t("Tempo")}</th>
                <th className="right">{t("Ritmo")}</th>
                <th className="right">{t("FC média")}</th>
                <th className="right">{t("Calorias")}</th>
                <th>{t("Treino")}</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((a) => (
                <tr
                  key={a.id}
                  className="row-click"
                  onClick={(e) => {
                    // a linha inteira abre a atividade; cliques em links/botões internos seguem o próprio caminho
                    if ((e.target as HTMLElement).closest("a,button")) return;
                    if (e.metaKey || e.ctrlKey) window.open(hrefFor(a), "_blank");
                    else navigate(hrefFor(a));
                  }}
                >
                  <td data-label={t("Data")} className="num when">
                    {fmtDayHeader(parseIso(a.local_date))} {localTime(a.start_time, a.timezone)}
                  </td>
                  <td className="what">
                    <Link to={hrefFor(a)} className="row-link">
                      {a.name}
                    </Link>
                    <span className="sub">{SPORT_LABEL[a.sport]}</span>
                  </td>
                  <td data-label={t("Distância")} className="right num m">{metersToKm(a.distance_m)}</td>
                  <td data-label={t("Tempo")} className="right num m">{formatDuration(a.moving_time_s)}</td>
                  <td data-label={t("Ritmo")} className="right num m">{formatPace(a.pace_s_km)}</td>
                  <td data-label={t("FC média")} className="right num m">{a.avg_hr ? `${a.avg_hr} bpm` : "—"}</td>
                  <td data-label={t("Calorias")} className="right num m">
                    {a.calories ? `${a.calories} kcal` : a.estimated_kcal ? <span title={t("Estimativa pelo seu peso e pelo esforço; não é medição.")}>≈ {a.estimated_kcal} kcal</span> : "—"}
                  </td>
                  <td data-label={t("Treino")} className="plan">
                    {a.planned_workout ? <Badge tone="ok">{a.planned_workout.title}</Badge> : <Badge>{t("Sem vínculo")}</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
